import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { userKeys } from '@/api/keys';
import { FavoriteCuisine, CuisineExpertiseItem } from '@/types/social';

// Re-export types for convenience
export type { FavoriteCuisine, CuisineExpertiseItem } from '@/types/social';

// Types (re-exported from original userApi for backward compatibility)
export interface UserStats {
  friendsCount: number;
  followersCount: number;
  followingCount: number;
  postsCount: number;
  checkinsCount: number;
  listsCount: number;
}

export interface User {
  id: number;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  location: string;
  dateOfBirth: string;
  bio: string;
  photoUrl: string;
  createdAt: string;
  membershipLevel: number;
  stats?: UserStats;
  favoriteCuisines?: FavoriteCuisine[];
  cuisineExpertise?: CuisineExpertiseItem[];
  phoneNumber?: string;
  phoneVerified?: boolean;
}

export interface VisitedCountriesResponse {
  countries: { countryShort: string; countryLong: string }[];
}

/**
 * Hook to get the list of countries a user has visited (via check-ins).
 */
export function useVisitedCountries(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.visitedCountries(userId),
    queryFn: () => apiFetch<VisitedCountriesResponse>(`/users/${userId}/visited_countries/`),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get the current logged-in user's profile
 */
export function useGetCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.current(),
    queryFn: () => apiFetch<User>('/users/'),
    enabled: options?.enabled,
  });
}
